/**
 * Deterministic scenery placement per chunk (PRD §10): vegetation, boulders,
 * drifting life and foreground silhouettes. Generated once per chunk from
 * WORLD_SEED + chunk index and cached.
 *
 * What gets placed depends on where you are. East of the trench the zones are
 * told apart far more by what lies on their floor than by the shape of the rock
 * — kelp, masonry, wreckage, chimneys, nothing at all — so the region drives
 * both the furniture and the palette.
 */

import { Rng } from '../core/noise';
import { WORLD, WORLD_SEED } from './config';
import { floorAt, isClear, seabedY } from './field';
import { PILLAR_X, VENT_X, pocketAt, regionAt } from './level';
import { ORE, oreWeights, pickOre } from './ore';
// Straight from the pure mesher, not via terrain.ts — props need the layer
// geometry, not the GPU streamer that happens to re-export it.
import { LAYERS, laneCeiling, laneZ, layerLift, type LayerSpec } from './mesher';
import { JELLY_SPECIES } from '../render/models';

export type PropKind =
  | 'kelp' | 'coral' | 'rock' | 'jelly' | 'fgrock' | 'fgkelp'
  | 'ruin' | 'debris' | 'monolith' | 'vent'
  /** An ore cluster. `variant` indexes `ORE`; a host boulder is placed with it. */
  | 'mineral';

export interface Prop {
  kind: PropKind;
  x: number;
  y: number;
  z: number;
  scale: number;
  rot: number;
  /** Per-instance animation phase. */
  phase: number;
  tint: [number, number, number];
  /** 0 = play layer, 1+ = parallax layers. Only affects how it is shaded. */
  layer: number;
  /** Which generated species/variant mesh to draw. */
  variant: number;
  /**
   * ORE ONLY, and only on the dominant cluster of a deposit: how many clusters
   * that deposit holds. Everything else leaves it undefined.
   *
   * It is what sizes the deposit's single point light, and it is on one prop
   * rather than all of them because a deposit is one glow. Thirteen lights
   * inside one another would cost thirteen against a budget of forty-eight and
   * look like one.
   */
  deposit?: number;
  /**
   * ORE ONLY: where the DEPOSIT is, on every cluster in it.
   *
   * `deposit` is on the dominant cluster alone, which is right for the light
   * and the sonar echo — one glow, one mark. Mining is the opposite question:
   * breaking a mother lode has to take all eleven clusters away, and the other
   * ten have no way back to the one that was shot. This is that way back, and
   * it is the SITE rather than an index because the save keys on it and an
   * index would renumber the moment a roll changed upstream.
   */
  siteX?: number;
  siteY?: number;
}

type RGB = [number, number, number];

const cache = new Map<number, Prop[]>();

/**
 * Is the parallax floor at `x` high enough to actually be seen past the play
 * layer? Parallax compresses the background toward the camera centre by roughly
 * the ratio of the two view distances, so a background ridge well below the
 * near seabed is hidden — and anything planted on it appears to float.
 */
function rootVisible(x: number, L: LayerSpec): boolean {
  const near = floorAt(x);
  const bg = floorAt(x + L.warp) + layerLift(x, L);
  // Camera sits ~20 m off the floor; that is where the compression is measured.
  const compress = 63 / (63 - L.zFront);
  const apparent = near + 20 + compress * (bg - near - 20);
  return apparent > near - 2;
}

/**
 * Floor height at `x`, or null when there is nothing to plant anything on.
 *
 * `floorAt` searches downward from a fixed height above the authored seabed, so
 * inside a city tower or a vent stack it starts in rock and converges on a
 * point buried in it. A "floor" that close to the top of the search is that
 * failure, not a ledge — and a prop seated there would be entombed.
 */
function seat(x: number): number | null {
  const y = floorAt(x);
  if (y < WORLD.yMin + 4 || y > seabedY(x) + 84) return null;
  return y;
}

/**
 * The same, on a parallax layer: the field sampled at `x + warp`, lifted.
 *
 * With one condition the play layer does not need. A parallax layer is meshed
 * on a deliberately coarse grid — `WORLD.cell * L.cellMul`, so 2.4x the play
 * cell on the middle layer and 3.8x on the far one — while `floorAt` is an
 * exact downward search on the density field. Anything thinner than that grid
 * can be found by the search and still be absent from the mesh, because the
 * marching squares never sample it.
 *
 * A blade seated on one of those is planted on ground that is never drawn, and
 * what you see is seagrass standing in open water with nothing underneath it.
 * The player's read of that — "we can't see the stones below them" — is exactly
 * right: the stone is in the field, it is just not in the mesh.
 *
 * So the seat has to be corroborated at the layer's own cell size. A ridge
 * broad enough to survive the coarse grid agrees with itself a cell to either
 * side. A spire or an arch crown has open water on both sides and is rejected.
 * One side dropping away is a cliff edge, which meshes fine and still grows.
 */
function bgSeat(x: number, L: LayerSpec): number | null {
  const w = x + L.warp;
  const y = floorAt(w);
  if (y < WORLD.yMin + 4 || y > seabedY(w) + 84) return null;
  const cell = WORLD.cell * L.cellMul;
  const tol = cell * 1.5;
  if (y - floorAt(w - cell) > tol && y - floorAt(w + cell) > tol) return null;
  const seat = y + layerLift(x, L);
  // And never in the air. `layerLift` clamps itself so the layer's *seabed*
  // stays 6 m under water, which says nothing about a feature standing proud of
  // that seabed: at x 321 the far layer's ground sits at -7.7 as intended and a
  // 10 m outcrop on top of it carries the seat to +2.7. Kelp planted there is
  // growing above the waterline. Same margin, applied to the thing actually
  // being planted on.
  if (seat > WORLD.surfaceY - 6) return null;
  return seat;
}

/**
 * Room for something tall. A ledge wide enough for a weed is not wide enough
 * for a ten-metre column, and the deep zones are full of crevices that look
 * like floor to a single downward search.
 */
function headroom(x: number, y: number, r: number): boolean {
  return isClear(x, y + r * 1.4, r);
}

/**
 * Weed between the camera and the boat.
 *
 * The frame has a far plane and a play plane and nothing in front, so the sub
 * is always the nearest thing in shot and the image has no near edge to it.
 * A few fronds crossing the lens fix that in a way no amount of background
 * work can: they are the only thing that proves there is water between the
 * viewer and the scene.
 *
 * They are unlit and near-black. That is not a shortcut — anything this close
 * is behind the lamp and gets no light at all, and a foreground element that
 * competes on brightness stops being a frame and becomes an obstruction. They
 * are seeded off the seabed and grown tall so they rise from the bottom edge,
 * which is where the eye tolerates them; hung in the middle of the frame the
 * same prop reads as a bug.
 */
function foregroundGrowth(out: Prop[], rng: Rng, x0: number, lush: number) {
  if (rng.next() > 0.36 * lush) return;
  const cx = x0 + rng.range(0, WORLD.chunkSize);
  const blades = rng.int(2, 5);
  for (let i = 0; i < blades; i++) {
    const x = cx + rng.range(-4.5, 4.5);
    out.push({
      kind: 'fgkelp',
      // Rooted below the seabed on purpose: the base is off the bottom of the
      // frame and only the upper stem is ever in shot, so it never has to
      // agree with ground the camera can also see.
      x, y: seabedY(x) - rng.range(6, 16),
      z: rng.range(11, 22),
      scale: rng.range(7, 15),
      rot: rng.range(-0.3, 0.3),
      phase: rng.range(0, Math.PI * 2),
      tint: [0.020, 0.032, 0.028],
      layer: 0,
      variant: 0,
    });
  }
}

/**
 * Boulders and wreckage at the back of the play layer.
 *
 * The frame had a near plane and a far plane and a hole between them. Scenery
 * sits within a few metres of the gameplay plane because that is where things
 * you can hit belong, and the first parallax layer starts 52 m further back, so
 * the eye jumps from the boat straight to the horizon with nothing to measure
 * the gap by.
 *
 * These fill it, and they stay inside the play layer's own extrusion (z down to
 * −16) rather than out in the gap. That is not laziness: the parallax layers
 * warp their x by hundreds of metres, so anything planted between them has no
 * ground under it, and seated on the near floor at a distance it projects
 * *above* the near seabed and floats. Depth here comes from being darker and
 * smaller, not from being further away — which is what distance looks like
 * anyway once the water has had its say.
 */
/**
 * How level the ground is at `x`, 1 flat and 0 a cliff.
 *
 * Gravel lands on flats and rolls off steep rock, and the floor's own slope is
 * three `floorAt` calls away — cheap enough to ask per grain.
 */
function flatness(x: number): number {
  const a = floorAt(x - 1.5), b = floorAt(x + 1.5);
  return Math.max(0, 1 - Math.abs(b - a) / 3);
}

/**
 * Gravel, shell and grit, scattered across the FULL depth of the floor.
 *
 * The seabed is a band nineteen metres deep in z, and everything that sat on it
 * was placed in one of two narrow slices — `scatterRocks` at z -10 to 2 and
 * `midgroundClutter` at -15 to -9 — so the floor had objects at the front and
 * objects at the back and nothing in between to say it had depth at all.
 *
 * Uses the debris meshes, which at 72 to 162 triangles are the cheapest props
 * in the game by an order of magnitude, and small enough that the eye reads
 * them as texture on the ground rather than as things.
 */
function gravel(out: Prop[], rng: Rng, x0: number, tint: RGB) {
  const count = rng.int(30, 50);
  for (let i = 0; i < count; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null) continue;
    // Steep ground keeps nothing. Sampled per grain, so a slope thins out
    // rather than ending on a line.
    if (rng.next() > flatness(x)) continue;
    const scale = rng.range(0.12, 0.5);
    out.push({
      kind: 'debris',
      x,
      // Sunk by a third, so it is bedded in the sediment and not resting on it.
      y: y - scale * 0.33,
      z: rng.range(-15, 3.2),
      scale,
      rot: rng.range(0, Math.PI * 2),
      phase: 0,
      tint: [tint[0], tint[1], tint[2]],
      layer: 0,
      variant: rng.int(0, 2),
    });
  }
}

function midgroundClutter(out: Prop[], rng: Rng, x0: number, wreckage: boolean) {
  const count = rng.int(2, 5);
  for (let i = 0; i < count; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null) continue;
    const junk = wreckage && rng.next() < 0.45;
    out.push({
      kind: junk ? 'debris' : 'rock',
      x, y: y + rng.range(-1.4, 0.5),
      z: rng.range(-15, -9),
      // Bigger than the near boulders. At the back of the layer they subtend
      // less, and a midground element that reads as small gravel adds noise
      // rather than depth.
      scale: junk ? rng.range(1.6, 3.4) : rng.range(2.6, 6.2),
      rot: rng.range(0, Math.PI * 2),
      phase: rng.range(0, Math.PI * 2),
      // Half the value of the near rock. The whole job of this plane is to be
      // a step between a lit foreground and a black horizon.
      tint: junk ? [0.085, 0.088, 0.092] : [0.098, 0.104, 0.110],
      layer: 0,
      variant: rng.int(0, 2),
    });
  }
}

/**
 * A full population for a parallax lane, without touching a single zone
 * function.
 *
 * Every zone writes `layer: 0` and seats against `floorAt(x)`, and rewriting
 * nine of them to take a lane would be nine chances to make lane 0 different.
 * But a lane IS the same world at an offset — drawn x is field x minus the
 * warp, drawn y is field y plus the lift — so the whole population can be
 * generated at the warped x by the existing code and then carried back into
 * drawn space. The seating comes out right for free, because it was seated
 * against the very ground that lands under it.
 *
 * The thin decorative scatter the generator emits for ITS background layers is
 * dropped: those describe lanes behind this one, which do not exist.
 */
function laneProps(index: number, lane: number): Prop[] {
  const L = LAYERS[lane]!;
  const dz = laneZ(lane);
  const out: Prop[] = [];
  // Generated one chunk further along the field, then walked back.
  const src = propsForChunk(index, 0, index * WORLD.chunkSize + L.warp);
  for (const q of src) {
    if (q.layer !== 0) continue;
    const x = q.x - L.warp;
    let y = q.y + layerLift(x, L);
    // ANYTHING THAT FLOATS HAS TO BE PUT BACK UNDER THE WATER.
    //
    // `layerLift` is capped so a lane's SEABED stays submerged, and that is
    // all it promises. A jellyfish is authored up to `span` metres above the
    // lane-0 floor and then lifted by 26 m on lane 1 or 54 on lane 2, and
    // nothing re-checks it — measured across the world, 61 of 626 background
    // jellyfish ended up above the waterline, the worst of them by 59.9 m.
    // That is the animal hanging in the sky.
    //
    // `laneCeiling` is already the answer and already carries the argument for
    // why it is not `surfaceY + layerLift`; the enemies and the shoals both
    // ask it. Props are the third caller it was always going to need. Seated
    // props are not clamped: rock, weed and ruins follow their ground, and the
    // lift is exactly what puts them on it.
    if (q.kind === 'jelly') y = Math.min(y, laneCeiling(x, L) - 2);
    out.push({ ...q, x, y, z: q.z + dz, layer: lane });
  }
  // THE HIDDEN SEAMS, which exist nowhere else.
  //
  // A pocket is a room behind a gate, on lane 1 or 2, and until now what was
  // in one was a salvage purse and a harder fight. The abyssal crystal is
  // `hiddenOnly`, so no depth on the open floor will ever produce one and the
  // only route to a shard of it is through a gate — which is the whole reason
  // to give the ordinary seams a 0.30 multiplier in here as well. A pocket is
  // not where you go for iron.
  //
  // Seeded off the pocket's own id rather than the chunk index alone, so the
  // contents of a room are a property of THAT ROOM and stay put no matter how
  // the chunking around it is later cut.
  const x0 = index * WORLD.chunkSize;
  const q = pocketAt(lane, x0 + WORLD.chunkSize * 0.5);
  if (q) {
    let h = 0;
    for (let i = 0; i < q.id.length; i++) h = (Math.imul(h, 31) + q.id.charCodeAt(i)) | 0;
    const prng = new Rng(WORLD_SEED ^ h ^ (index * 26417));
    // A pocket spans about 360 m, so seven or eight chunks. Two rolls at 0.11
    // is a couple of deposits in the room, and the pocket bias in `rich` makes
    // them the big ones — which is what a room behind a gate should hold.
    oreSeams(out, prng, x0, regionAt(x0 + L.warp).name, true, 2, 0.11,
             (x) => bgSeat(x, L), lane, dz);
  }
  return out;
}

/**
 * ORE SEAMS, and the boulder each one sits on.
 *
 * The clusters are modelled INTO a rock rather than balanced on one — their
 * lowest facets are a cut face, measured at y 0.00-0.17 against a boulder top
 * of 0.44-0.60 in the source art. So the host is placed HERE, as a pair, and
 * not looked for among the boulders the region generators already scattered.
 * Two reasons, and the second is the real one:
 *
 *   Those boulders are placed by nine different region functions at scales
 *   from 1.4 to 4.6, some of them half-buried, and none of them records where
 *   its own top ended up. Seating a cluster on one would mean re-deriving that
 *   from the prop list every time.
 *
 *   And a seam is a thing you should be able to READ from across the frame. A
 *   boulder with ore on it wants to be a boulder-sized boulder standing a
 *   little clear of the floor, which is a narrower range than scenery rock
 *   uses. Authoring the pair together is what lets the silhouette say "there
 *   is something on that one" before the glow is close enough to see.
 *
 * `buildRock` is a jittered unit sphere flattened to 0.8 in y, so a host of
 * scale s tops out around 0.8s and the cluster is dropped at 0.72 of that.
 * The remaining 0.06-0.2 s is what buries the cut, and the jitter — 0.84 to
 * 1.16 per vertex — is what stops eight identical seatings reading as eight
 * copies of one prop.
 */
/**
 * Can a submarine get from here to open water?
 *
 * A flood fill through the density field, coarse and capped. Coarse because a
 * 3.5 m grid is finer than any gap a 7.2 m boat could use anyway, and capped
 * because the answer for a sealed bubble has to be cheap — that is the case
 * this exists to reject, and it is the one that would otherwise search for
 * ever. 260 cells is about 45 m of reach in any direction.
 *
 * "Out" is one cell above the column's own first surface. `floorAt` stops at
 * the roof of a cave, so being above it means the roof is behind you.
 */
function escapes(x0: number, y0: number): boolean {
  const step = 3.5;
  const seen = new Set<string>();
  const qx: number[] = [x0], qy: number[] = [y0];
  seen.add(`${Math.round(x0 / step)},${Math.round(y0 / step)}`);
  for (let head = 0; head < qx.length && head < 260; head++) {
    const x = qx[head]!, y = qy[head]!;
    if (y > floorAt(x)) return true;
    for (let d = 0; d < 4; d++) {
      const nx = x + (d === 0 ? step : d === 1 ? -step : 0);
      const ny = y + (d === 2 ? step : d === 3 ? -step : 0);
      const k = `${Math.round(nx / step)},${Math.round(ny / step)}`;
      if (seen.has(k)) continue;
      seen.add(k);
      if (isClear(nx, ny, 2.0)) { qx.push(nx); qy.push(ny); }
    }
  }
  return false;
}

/**
 * THE FLOOR OF A CHAMBER, if this column has one under its surface.
 *
 * `floorAt` searches DOWNWARD from 90 m above the authored seabed and stops at
 * the first solid thing it converges on — which inside a grotto is the roof,
 * not the room. So `seat` has never been able to return a point inside a cave,
 * and no ore, weed or boulder has ever been placed in one. Every deposit in
 * the game is on open ground because open ground is the only ground the seater
 * can see.
 *
 * That is the wrong way round for ore twice. A chamber is where a player is
 * rewarded for having gone somewhere; and the "big deposit" bonus was asking
 * for clear water ABOVE the site, which is the one thing a cave does not have,
 * so caves were being penalised for being caves.
 *
 * This continues the search. Drop through the rock under the first surface
 * until the density says water again, then let `floorAt` find that void's own
 * floor from inside it. Two passes, so a stack of galleries resolves to one of
 * them rather than giving up at the first ceiling.
 */
function chamberSeat(x: number): number | null {
  let ceiling = floorAt(x);
  for (let pass = 0; pass < 2; pass++) {
    // Down through the rock. Bounded, because a column that is solid to the
    // bottom of the world is the common case and must cost almost nothing.
    let y = ceiling - 2;
    let steps = 0;
    while (y > WORLD.yMin + 8 && steps < 48 && !isClear(x, y, 2.2)) {
      y -= 2.4;
      steps++;
    }
    if (y <= WORLD.yMin + 8 || steps >= 48) return null;
    // Water. Its floor is the next surface below, found from inside the void.
    const floor = floorAt(x, y);
    if (floor <= WORLD.yMin + 4) return null;
    // A ROOM YOU CAN GET INTO, which is not the same as a room.
    //
    // The density field is full of sealed bubbles and the first version took
    // any of them: measured, five of sixteen chamber deposits sat inside solid
    // rock with no way in. That is ore that does not render, and from the
    // cockpit it looks exactly like the feature being broken.
    //
    // Width was the cheap proxy and it only got five down to three, because a
    // sealed bubble can perfectly well be fifteen metres across. Nothing short
    // of connectivity actually answers "can a submarine reach this", so it is
    // answered — `escapes` is a bounded flood fill, and the bound is what
    // makes it affordable.
    if (floor < y - 5 && isClear(x, floor + 3.5, 2.6)
        && escapes(x, floor + 3.5)) {
      return floor;
    }
    ceiling = floor;
  }
  return null;
}

const oreW: number[] = new Array(ORE.length).fill(0);

/**
 * DEPOSIT SIZES. How many clusters stand together, and on how many boulders.
 *
 * Scaling ONE crystal up to make a seam read was the wrong lever, and the give
 * away is what a big one looked like: a single slab, the same silhouette as a
 * small one with the numbers changed. Ore does not occur as one large crystal.
 * It occurs as a lot of them, mostly small, a few large, all on the same rock —
 * so a rich seam should differ from a poor one in COUNT, and only then in size.
 *
 * The count is also the reward. Each cluster is its own mineable node, so a
 * mother lode pays twelve times a lone one without the economy needing to know
 * that deposits exist at all.
 */
const DEPOSITS = [
  { name: 'lone',   min: 1, max: 1,  hosts: 1, weight: 0.45 },
  { name: 'seam',   min: 2, max: 3,  hosts: 1, weight: 0.33 },
  { name: 'vein',   min: 4, max: 6,  hosts: 2, weight: 0.17 },
  { name: 'mother', min: 8, max: 11, hosts: 3, weight: 0.05 },
];

/**
 * ORE SEAMS, and the boulders each one sits on.
 *
 * The clusters are modelled INTO a rock rather than balanced on one — their
 * lowest facets are a cut face, measured at y 0.00-0.17 against a boulder top
 * of 0.44-0.60 in the source art. So the hosts are placed HERE, with the ore,
 * and not looked for among the boulders the region generators already
 * scattered. Two reasons, and the second is the real one:
 *
 *   Those boulders come from nine different region functions at scales from
 *   1.4 to 4.6, some half-buried, and none records where its own top ended up.
 *
 *   And a deposit should be READABLE from across the frame. That wants a
 *   boulder-sized boulder standing a little clear of the floor, which is a
 *   narrower range than scenery rock uses, and it wants the ore arranged on it
 *   rather than dropped at its centre.
 *
 * `buildRock` is a jittered unit sphere flattened to 0.8 in y, so a host of
 * scale s is a dome of radius s topping out at 0.8s — which means a cluster
 * set d metres off the centre seats at `0.8s * sqrt(1 - (d/s)^2)` and the
 * arrangement follows the rock instead of floating over it.
 */
function oreSeams(out: Prop[], rng: Rng, x0: number, region: string,
                  hidden: boolean, tries: number, chance: number,
                  seatAt: (x: number) => number | null = seat,
                  layer = 0, dz = 0) {
  for (let i = 0; i < tries; i++) {
    if (rng.next() >= chance) continue;
    // SEVERAL CANDIDATES PER ROLL, and this is not a micro-optimisation — it
    // is the difference between the depth progression existing and not.
    //
    // One x per roll meant the seat and headroom tests decided WHERE ore was,
    // not just whether a particular spot would do. Both fail far more often on
    // steep or enclosed ground than on open floor, so the deep half of the
    // world — trenches, vent fields, the chambers — was quietly rejecting most
    // of its rolls while the shelf accepted nearly all of them. The census was
    // unambiguous: 29 of 43 deposits on the shelf, ONE in the thermal vents,
    // and biological ore, whose home is the vents, occurring exactly zero
    // times in an eleven-kilometre world.
    //
    // Retrying moves the terrain's opinion back to where it belongs. `chance`
    // decides how much ore there is; the ground decides which metre of a
    // chunk it stands on, and no longer which regions have any.
    let x = 0;
    let y: number | null = null;
    let chamber = false;
    // A CAVE FIRST, AND IT GETS ITS OWN ATTEMPTS.
    //
    // Folded into the surface loop this had exactly one chance: the probe
    // missed, the very next line found open floor, and the loop ended. The
    // effective rate was 0.7 x 0.22 = 15%, which is a cave being represented
    // in proportion to how much of the world is cave. A chamber should be
    // OVER-represented — it is a place you had to go into, and the payoff for
    // that is the whole reason to put ore in one. Two attempts at 22% is 39%.
    //
    // Play layer only: `chamberSeat` reads the density field directly, and a
    // background lane is a warped column that field knows nothing about.
    if (layer === 0) {
      for (let a = 0; a < 2 && y === null; a++) {
        const cx = x0 + rng.range(0, WORLD.chunkSize);
        const cy = chamberSeat(cx);
        if (cy !== null) { x = cx; y = cy; chamber = true; }
      }
    }
    for (let a = 0; a < 6 && y === null; a++) {
      x = x0 + rng.range(0, WORLD.chunkSize);
      y = seatAt(x);
      // Headroom is a play-layer question. A pocket seam is inside a room that
      // was carved to be flown through, and the lane field is not what
      // `isClear` reads, so asking it there would refuse every site.
      if (y !== null && layer === 0 && !headroom(x, y, 3.0)) y = null;
    }
    if (y === null) continue;
    const depth = -y;
    oreWeights(depth, region, hidden, oreW);
    const k = pickOre(oreW, rng.next());
    if (oreW[k]! <= 0) continue;

    // THE BIG ONES ARE DEEPER AND BEHIND GATES. A mother lode on the shelf
    // would be the first thing a new player met and the last time the word
    // meant anything; the same roll is tilted toward the rich end by depth and
    // again by being in a pocket, so finding one stays an event.
    // AND A BIG ONE WANTS A ROOM TO BE IN.
    //
    // Depth and a gate are two thirds of it; the third is space. A mother lode
    // wedged under a two-metre overhang is a mother lode nobody can see, while
    // the same thing standing on the floor of an open vent cavern is the whole
    // reason to have gone down there. `headroom` is already the question — how
    // much clear water is above this point — so asking it at three radii turns
    // "is there room here" into a number the roll can use.
    // A ROOM, and a cave counts as the best kind.
    //
    // This asked `headroom` twice and nothing else, which reads as "is there
    // open water above" — true of the seabed and false of every grotto in the
    // game. So the one place a player would most like to be paid for having
    // found it scored zero. A chamber is now the top of the scale and open
    // headroom is what is left.
    const room = chamber ? 0.38
      : layer === 0
        ? (headroom(x, y, 6) ? 0.10 : 0) + (headroom(x, y, 12) ? 0.16 : 0)
        : 0.16;
    // CAPPED, because the three bonuses stack. A deep pocket in an open
    // cavern reached 1.33 and the skew below turned that into a mother lode
    // twice as often as a lone crystal — the census said 10 mothers to 5 lones,
    // which is the rare row outnumbering the common one.
    const rich = Math.min(1,
      Math.min(1, depth / 2200) * 0.5 + (hidden ? 0.35 : 0) + room);
    let roll = rng.next();
    // Skewed rather than reweighted: a power below 1 pushes a uniform roll
    // toward its top end, which is where the rare rows are. One line, and the
    // weights stay readable as the numbers they were authored as.
    roll = Math.pow(roll, 1 / (1 + rich * 0.9));
    let acc = 0;
    let d = DEPOSITS[0]!;
    for (const q of DEPOSITS) { acc += q.weight; if (roll < acc) { d = q; break; } d = q; }
    const count = rng.int(d.min, d.max);

    const z = rng.range(-7, 1.5) + dz;
    // The hosts. First at the site, the rest crowded around it and a little
    // smaller, so a large deposit is a pile of rock rather than one big ball.
    const hosts: { x: number; y: number; z: number; s: number }[] = [];
    for (let h = 0; h < d.hosts; h++) {
      const hs = rng.range(1.35, 2.5) * (h === 0 ? 1 : rng.range(0.6, 0.9));
      const ang = rng.range(0, Math.PI * 2);
      const off = h === 0 ? 0 : rng.range(1.1, 2.4);
      const hx = x + Math.cos(ang) * off;
      const hz = z + Math.sin(ang) * off * 0.5;
      // THE HOST GOES WHERE THE SITE IS, and re-seating it was the whole of
      // why no deposit ever appeared in a cave.
      //
      // `seatAt` is the SURFACE seater. Calling it here threw away whatever
      // `chamberSeat` had just found and put the boulder back on the roof —
      // the chamber's y survived only long enough to compute a depth. The
      // first host now takes the site's own y, and inside a chamber the others
      // take it too: a cave floor is locally flat, and re-probing per host
      // would land the second boulder in a different gallery from the first.
      const hy = (h === 0 || chamber ? y : (seatAt(hx) ?? y))
        + hs * 0.18 + (chamber && h > 0 ? rng.range(-0.3, 0.3) : 0);
      hosts.push({ x: hx, y: hy, z: hz, s: hs });
      out.push({
        kind: 'rock',
        x: hx, y: hy, z: hz,
        scale: hs,
        rot: rng.range(0, Math.PI * 2),
        phase: 0,
        // A hair darker than scenery rock. The ore is the bright thing on it
        // and wants something to be bright against.
        tint: [0.20 + rng.range(0, 0.05), 0.20 + rng.range(0, 0.05),
               0.21 + rng.range(0, 0.05)],
        layer,
        variant: 0,
      });
    }

    // THE CLUSTERS. The first is the dominant one and carries the deposit's
    // light; the rest are its offspring, a third to three quarters its size,
    // which is what makes a group read as one seam rather than as N props that
    // happen to be adjacent.
    const lead = hosts[0]!;
    const base = lead.s * rng.range(1.25, 1.8) * (1 + Math.min(count, 12) * 0.03);
    for (let c = 0; c < count; c++) {
      const host = hosts[rng.int(0, hosts.length - 1)]!;
      // Polar on the host's own dome, kept inside 0.62 of its radius so a
      // cluster never hangs off the shoulder into open water.
      const a = rng.range(0, Math.PI * 2);
      const rad = (c === 0 ? rng.range(0, 0.18) : Math.sqrt(rng.next()) * 0.62) * host.s;
      const cx = host.x + Math.cos(a) * rad;
      const cz = host.z + Math.sin(a) * rad * 0.5;
      // The dome's own height at that offset, then sunk to bury the cut.
      const dome = 0.8 * host.s * Math.sqrt(Math.max(0, 1 - (rad / host.s) ** 2));
      out.push({
        kind: 'mineral',
        x: cx,
        y: host.y + dome * 0.86,
        z: cz,
        scale: c === 0 ? base : base * rng.range(0.32, 0.78),
        // Yawed only. A crystal cluster grows up out of its matrix; tipping one
        // over reads as debris that happens to be glowing.
        rot: rng.range(0, Math.PI * 2),
        phase: rng.range(0, Math.PI * 2),
        tint: [1, 1, 1],
        layer,
        variant: k,
        siteX: x,
        siteY: y,
        // ONE LIGHT PER DEPOSIT, ON THE DOMINANT CLUSTER, sized by how many
        // clusters are in it. A light each would put thirteen in a mother lode
        // against a budget of forty-eight on the lowest tier, and they would
        // all be inside one another anyway — a deposit is one glow, not
        // thirteen. Zero on the rest, which is what stops them adding any.
        deposit: c === 0 ? count : 0,
      });
    }
  }
}

export function propsForChunk(index: number, lane = 0, originX?: number): Prop[] {
  // Lane 0 keeps the identity it always had; the key only widens.
  const key = index * 8 + lane;
  const hit = originX === undefined ? cache.get(key) : undefined;
  if (hit) return hit;
  if (lane !== 0) {
    const made = laneProps(index, lane);
    if (cache.size > 900) cache.clear();
    cache.set(key, made);
    return made;
  }

  const rng = new Rng(WORLD_SEED ^ (index * 40503) ^ 0x5eed);
  const x0 = originX ?? index * WORLD.chunkSize;
  const out: Prop[] = [];
  const mid = x0 + WORLD.chunkSize * 0.5;
  const depth = -seabedY(mid);

  // One region per chunk, decided at its midpoint. A chunk straddling a border
  // that mixed two palettes would read as a mistake rather than a transition.
  switch (regionAt(mid).name) {
    case 'CORAL GARDEN': coralGarden(out, rng, x0); break;
    case 'SEAGRASS MEADOW': seagrassMeadow(out, rng, x0); break;
    case 'KELP FOREST': kelpForest(out, rng, x0); break;
    case 'SUNKEN CITY': sunkenCity(out, rng, x0); break;
    // The berth is the far end of the same battlefield; same wreckage.
    case 'MILITARY ZONE':
    case 'CAPITAL BERTH': militaryZone(out, rng, x0); break;
    case 'THERMAL VENTS': thermalVents(out, rng, x0); break;
    case 'THE ABYSS': theAbyss(out, rng, x0); break;
    case 'ANCIENT STRUCTURE': ancientStructure(out, rng, x0); break;
    default: shelfScenery(out, rng, x0, depth);
  }

  // Every zone gets all three planes. The near one thins where the water is
  // meant to feel empty — the abyss and the last zone earn their emptiness —
  // but never to nothing, because the frame needs something in front of the
  // boat everywhere.
  const region = regionAt(mid).name;
  const empty = region === 'THE ABYSS' || region === 'ANCIENT STRUCTURE';
  foregroundGrowth(out, rng, x0, empty ? 0.35 : region === 'KELP FOREST' ? 1.5 : 1);
  midgroundClutter(out, rng, x0,
    region === 'MILITARY ZONE' || region === 'CAPITAL BERTH' ||
    region === 'SUNKEN CITY' || region === 'MINEFIELD' || region === 'PATROL ZONE' ||
    region === 'WRECK SITE');
  // Grit across the whole depth of the floor, tinted to the sediment rather
  // than to the rock it came off.
  gravel(out, rng, x0, [0.30, 0.29, 0.26]);
  // ORE, on the open floor.
  //
  // THE CHANCE IS THE WHOLE CONTROL, and the first version did not have one.
  // Two unconditional rolls per chunk sounded sparse — a seam still has to
  // pass a seat test, a headroom test and a depth window — and the census said
  // 653 nodes across 11.2 km, one every seventeen metres. That is not a find,
  // it is gravel. The seat and headroom tests reject far less than they look
  // like they should, because the generator is already choosing x on ground it
  // knows is floor.
  //
  // 0.16 put a lone node every 150 m. A site is now a DEPOSIT of two or three
  // clusters on average, so the same chance would treble the ore in the world.
  // 0.115 lands near one deposit every 250 m — further apart than a single
  // pebble could afford to be and exactly what a landmark can.
  //
  // Not lower, and the reason is a census rather than a feel: at 0.09 there
  // were 53 sites in the whole world and BIO NEVER OCCURRED ONCE. A seam that
  // is 3% of the table needs enough rolls to show up at all, and a mineral a
  // playthrough might never meet is not rare, it is missing.
  oreSeams(out, rng, x0, region, false, 2, 0.115);
  // And something alight in the distance, once the water is dark enough for it
  // to be the only thing back there.
  //
  // BLUE-GREEN, AND NOT BY TASTE. Water eats red first and fastest — at the
  // 240 m band a pink glow arrives at sixty metres with a fiftieth of its red
  // left, so it lands as a washed grey-violet with the least luminance of any
  // colour tried, while the same energy spent on cyan arrives at nearly twice
  // the brightness. The lane cue takes another bite out of red on top.
  //
  // Which is why real deep-sea bioluminescence is overwhelmingly blue-green:
  // it is the only part of the spectrum that travels. A pink animal down there
  // would be invisible to everything including itself. The violet keeps its
  // character by being blue-dominant rather than red-dominant.
  bgJellies(out, rng, x0, depth, (r) => (
    r.next() < 0.34
      ? [0.30, 0.95, 0.85]
      : r.next() < 0.5 ? [0.62, 0.45, 1.00] : [0.35, 0.60, 1.00]
  ));

  if (originX !== undefined) return out;      // a lane's raw pass; not cached here
  if (cache.size > 900) cache.clear();
  cache.set(key, out);
  return out;
}

// ---------------------------------------------------------------------------
// The shelf: everything west of the kelp forest
// ---------------------------------------------------------------------------

/**
 * THE CORAL GARDEN. The one zone that is about colour.
 *
 * Every other place in this game is a value study — rock, silt and a lamp — and
 * the player asked for a reef after saying the coloured corals were the best
 * thing they had seen. So this leans the whole way: heads packed dense enough
 * to hide the ground, hues drawn from a reef palette rather than jittered
 * around one, and stands built as MOUNDS so the eye reads structure instead of
 * confetti scattered on a floor.
 *
 * It sits at -1900..-900 because the survey put the floor there at a mean of
 * -25 m with headlands reaching -1.5. Coral needs light; that is the shallowest
 * broad shelf in the world and it is why the zone is here.
 */
const REEF_HUES: [number, number, number][] = [
  [0.95, 0.34, 0.42],   // fire coral
  [1.00, 0.55, 0.22],   // orange sponge
  [0.98, 0.80, 0.28],   // yellow polyps
  [0.42, 0.82, 0.62],   // green staghorn
  [0.34, 0.66, 0.98],   // blue ridge
  [0.78, 0.42, 0.94],   // violet fan
];

/**
 * A reef hue appropriate to the depth.
 *
 * `REEF_HUES` runs warm to cold, which is also how a real reef runs down a
 * wall: the reds and oranges belong to the top, where there is still red light
 * to reflect, and what survives further down is what makes its OWN light —
 * greens, blues and violets. A three-wide window slides along the list as the
 * water deepens, so neighbouring stands share a palette rather than each
 * picking from the whole spectrum, and the zone reads as one place.
 *
 * This is why the vivid coral is no longer confined to CORAL GARDEN. It was one
 * region out of nine, so the most striking thing in the game was a few hundred
 * metres of a very long dive; everywhere else got two hand-mixed browns.
 */
function reefTint(rng: Rng, depth: number): RGB {
  const t = Math.max(0, Math.min(1, depth / 900));
  const lo = Math.round(t * (REEF_HUES.length - 3));
  const c = REEF_HUES[Math.min(REEF_HUES.length - 1, lo + rng.int(0, 2))]!;
  return [
    c[0] * rng.range(0.80, 1.14),
    c[1] * rng.range(0.80, 1.14),
    c[2] * rng.range(0.80, 1.14),
  ];
}

function coralGarden(out: Prop[], rng: Rng, x0: number) {
  // Mounds, not a scatter. Each is a cluster with a size and a dominant hue, so
  // the reef has landmarks in it and two chunks never look the same.
  const mounds = rng.int(5, 8);
  for (let m = 0; m < mounds; m++) {
    const cx = x0 + rng.range(0, WORLD.chunkSize);
    const spread = rng.range(6, 17);
    const base = REEF_HUES[rng.int(0, REEF_HUES.length - 1)];
    const heads = rng.int(16, 34);
    for (let i = 0; i < heads; i++) {
      const x = cx + rng.range(-spread, spread);
      const y = seat(x);
      if (y === null) continue;
      // Mostly the mound's own hue, occasionally a neighbour's — a real reef is
      // one species winning, not an even mix.
      const c = rng.next() < 0.78 ? base : REEF_HUES[rng.int(0, REEF_HUES.length - 1)];
      const lift = 1 - Math.abs(x - cx) / (spread + 1);
      out.push({
        kind: 'coral',
        x, y: y - 0.3,
        z: rng.range(-9, 5),
        // Tallest at the centre of the mound, so it has a silhouette.
        scale: rng.range(0.8, 2.2) * (0.6 + lift * 0.9),
        rot: rng.range(0, Math.PI),
        phase: rng.range(0, Math.PI * 2),
        tint: [
          c[0] * rng.range(0.82, 1.12),
          c[1] * rng.range(0.82, 1.12),
          c[2] * rng.range(0.82, 1.12),
        ],
        layer: 0,
        variant: 0,
      });
    }
  }

  // Weed between the heads, short and pale — reef algae, not kelp.
  const tufts = rng.int(6, 12);
  for (let t = 0; t < tufts; t++) {
    const cx = x0 + rng.range(0, WORLD.chunkSize);
    for (let i = 0; i < rng.int(3, 7); i++) {
      const x = cx + rng.range(-4, 4);
      const y = seat(x);
      if (y === null) continue;
      out.push({
        kind: 'kelp',
        x, y: y - 0.4,
        z: rng.range(-8, 4),
        scale: rng.range(1.1, 2.4),
        rot: rng.range(-0.5, 0.5),
        phase: rng.range(0, Math.PI * 2),
        tint: [0.34 + rng.range(0, 0.18), 0.52 + rng.range(0, 0.2), 0.24 + rng.range(0, 0.14)],
        layer: 0,
        variant: 0,
      });
    }
  }

  // Bommies: isolated coral boulders that break the skyline.
  for (let i = 0; i < rng.int(1, 3); i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null || !headroom(x, y, 3.5)) continue;
    out.push({
      kind: 'rock',
      x, y: y + rng.range(-0.4, 0.6),
      z: rng.range(-9, 2),
      scale: rng.range(2.2, 4.6),
      rot: rng.range(0, Math.PI * 2),
      phase: 0,
      // Warm, because everything here is encrusted rather than bare.
      tint: [0.30, 0.24, 0.20],
      layer: 0,
      variant: 0,
    });
  }
}

/**
 * THE SEAGRASS MEADOW. The shallowest water in the game, and the emptiest.
 *
 * The reef is dense and loud; this is the opposite and is placed beyond it on
 * purpose, so the two do not compete. Blades in continuous beds rather than
 * clumps — a meadow is a surface, not a set of objects — and almost nothing
 * else in the water, which is what makes it read as calm rather than as sparse.
 *
 * At -3000..-1900 the floor is at a mean of -22 m with headlands at -2, so the
 * caustics reach the ground here and the beds are lit from above all day.
 */
function seagrassMeadow(out: Prop[], rng: Rng, x0: number) {
  // Continuous beds, walked along the floor rather than dropped in clumps.
  // Walked at 0.8 m with two or three blades a step. The first pass used 1.5 m
  // and one blade, which is about fifteen blades in a 48 m chunk — a scatter,
  // and it read as one. A meadow has to be a SURFACE: the eye needs the ground
  // to disappear under it before the word means anything.
  const step = 0.8;
  for (let x = x0; x < x0 + WORLD.chunkSize; x += step) {
    // Beds thin and thicken along the floor instead of stopping dead.
    const bed = 0.5 + 0.5 * Math.sin(x * 0.011 + Math.sin(x * 0.0031) * 2.2);
    if (rng.next() > 0.35 + bed * 0.6) continue;
    const px = x + rng.range(-0.7, 0.7);
    const y = seat(px);
    if (y === null) continue;
    for (let b = rng.int(1, 2); b > 0; b--) out.push({
      kind: 'kelp',
      x: px + rng.range(-0.6, 0.6), y: y - 0.5,
      z: rng.range(-12, 7),
      // Short and even. Tall blades here would read as kelp and this is not it.
      scale: rng.range(1.5, 3.1) * (0.7 + bed * 0.5),
      rot: rng.range(-0.28, 0.28),
      phase: rng.range(0, Math.PI * 2),
      // Fresh green, and much lighter than the deep kelp — this water is bright.
      tint: [0.26 + rng.range(0, 0.12), 0.54 + rng.range(0, 0.22), 0.22 + rng.range(0, 0.12)],
      layer: 0,
      variant: 0,
    });
  }

  // A few worn boulders standing out of the grass, and nothing else.
  for (let i = 0; i < rng.int(1, 4); i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null) continue;
    out.push({
      kind: 'rock',
      x, y: y + rng.range(-0.8, 0.3),
      z: rng.range(-11, 2),
      scale: rng.range(1.4, 3.8),
      rot: rng.range(0, Math.PI * 2),
      phase: 0,
      tint: [0.23, 0.24, 0.22],
      layer: 0,
      variant: 0,
    });
  }

  // Foreground blades, close to the lens. The meadow's whole trick is depth
  // through one repeated object, so the near plane has to carry some of it.
  if (rng.next() < 0.75) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    for (let i = 0; i < rng.int(2, 5); i++) {
      out.push({
        kind: 'fgkelp',
        x: x + rng.range(-6, 6),
        y: seabedY(x) - rng.range(2, 9),
        z: rng.range(12, 22),
        scale: rng.range(3.0, 6.0),
        rot: rng.range(-0.3, 0.3),
        phase: rng.range(0, Math.PI * 2),
        tint: [0.055, 0.10, 0.05],
        layer: 0,
        variant: 0,
      });
    }
  }
}

function shelfScenery(out: Prop[], rng: Rng, x0: number, depth: number) {
  // Vegetation thins out with depth and stops entirely below the photic zone.
  // Kelp grows in stands, not evenly scattered — a few clumps per chunk.
  const kelpDensity = Math.max(0, 1 - depth / 300);
  const stands = Math.round(kelpDensity * rng.range(0.6, 3.4));
  for (let s = 0; s < stands; s++) {
    const cx = x0 + rng.range(0, WORLD.chunkSize);
    const standScale = rng.range(0.7, 1.5);
    const blades = rng.int(4, 11);
    for (let i = 0; i < blades; i++) {
      const x = cx + rng.range(-5.5, 5.5);
      const y = floorAt(x);
      if (y < WORLD.yMin + 4) continue;
      out.push({
        kind: 'kelp',
        x, y: y - 0.5,
        z: rng.range(-11, 6),
        scale: rng.range(2.4, 6.5) * standScale * (0.45 + kelpDensity * 0.75),
        rot: rng.range(-0.35, 0.35),
        phase: rng.range(0, Math.PI * 2),
        tint: [0.20 + rng.range(0, 0.13), 0.38 + rng.range(0, 0.24), 0.16 + rng.range(0, 0.12)],
        layer: 0,
        variant: 0,
      });
    }
  }

  // Was 2-7. These are the only colour on the shelf and they now glow, so they
  // are worth seeing more than once a chunk.
  const coralCount = rng.int(5, 12);
  for (let i = 0; i < coralCount; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = floorAt(x);
    if (y < WORLD.yMin + 4) continue;
    out.push({
      kind: 'coral',
      x, y: y - 0.2,
      z: rng.range(-7, 4),
      scale: rng.range(0.9, 2.6),
      rot: rng.range(0, Math.PI),
      phase: rng.range(0, Math.PI * 2),
      // One palette that walks down the whole dive, instead of a warm brown
      // and a violet with a hard switch at 500 m between them.
      tint: reefTint(rng, depth),
      layer: 0,
      variant: 0,
    });
  }

  const rockCount = rng.int(1, 4);
  for (let i = 0; i < rockCount; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = floorAt(x);
    if (y < WORLD.yMin + 4) continue;
    out.push({
      kind: 'rock',
      x, y: y + rng.range(-0.6, 0.4),
      z: rng.range(-10, 2),
      scale: rng.range(0.8, 3.4),
      rot: rng.range(0, Math.PI * 2),
      phase: 0,
      tint: [0.2, 0.21, 0.22],
      layer: 0,
      variant: 0,
    });
  }

  // Foreground silhouettes — close to the camera, deliberately unlit.
  if (rng.next() < 0.45) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    out.push({
      kind: 'fgrock',
      x,
      // Kept low in frame so they read as framing, not obstructions.
      y: rng.range(seabedY(x) - 10, seabedY(x) + 6),
      z: rng.range(13, 23),
      scale: rng.range(1.8, 4.4),
      rot: rng.range(0, Math.PI * 2),
      phase: 0,
      tint: [0.075, 0.09, 0.10],
      layer: 0,
      variant: 0,
    });
  }

  // Decorative medusae only — anything in the play plane is a live creature
  // owned by the actor system, so it can be shot and can sting.
  // BACK TO ONE TO THREE. Three to six was mine and it was wrong: `shelfScenery`
  // is the `default` arm of the region switch, so it dresses every zone that is
  // not one of the eight named ones — the Abyssal Chamber among them — and
  // tripling it there put roughly five decorative medusae per chunk in the band
  // BEHIND the play slab. Against seventeen live animals in a 480 m window that
  // is fifty pieces of scenery, and the frame reads as decoration with the
  // player flying through the middle of it. The near band needed better
  // animals, not more furniture.
  const jellyCount = depth > 180 ? rng.int(0, 1) : rng.int(0, 1);
  for (let i = 0; i < jellyCount; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const top = seabedY(x);
    const y = rng.range(top + 6, Math.min(WORLD.surfaceY - 8, top + 70));
    if (!isClear(x, y, 3)) continue;
    out.push({
      kind: 'jelly',
      x, y,
      // BACK, AND BIG.
      //
      // I moved this band forward to -26 to fix "the near jellyfish are small",
      // and that was the wrong fix for the right complaint. The small near
      // animals were the LIVE ones — `configure` had no case for them and they
      // were all coming out at the default size of 1 — and pulling the
      // decoration forward to compensate put a crowd of UNSHOOTABLE medusae in
      // the closest, brightest part of the frame. "Loads of them and I can't
      // kill any" is that change, precisely.
      //
      // So it goes back behind, near enough to lane 1 at -55 to read as the
      // same distance, and stays big because it is far. What is near, bright
      // and worth aiming at is now the live population, which is what the
      // player was reaching for all along.
      z: rng.range(-66, -42),
      scale: rng.range(1.6, 3.8),
      rot: 0,
      phase: rng.range(0, Math.PI * 2),
      tint: [0.25, 0.7, 1.0],
      layer: 0,
      variant: rng.int(0, JELLY_SPECIES - 1),
    });
  }

  addBackgroundGrowth(out, rng, x0, depth);
}

// ---------------------------------------------------------------------------
// KELP FOREST — the beauty shot
// ---------------------------------------------------------------------------

/**
 * Warmer and far more varied than the shelf stands, with the occasional golden
 * frond: a single green repeated two hundred times reads as wallpaper, and this
 * is the one place the player is meant to stop and look.
 */
function kelpTint(rng: Rng, dim = 1): RGB {
  if (rng.next() < 0.11) {
    return [
      (0.60 + rng.range(0, 0.28)) * dim,
      (0.50 + rng.range(0, 0.22)) * dim,
      (0.12 + rng.range(0, 0.10)) * dim,
    ];
  }
  return [
    (0.20 + rng.range(0, 0.22)) * dim,
    (0.42 + rng.range(0, 0.30)) * dim,
    (0.13 + rng.range(0, 0.16)) * dim,
  ];
}

function kelpForest(out: Prop[], rng: Rng, x0: number) {
  // The depth falloff used everywhere else would leave this stretch nearly
  // bare at −300 m. Here the forest *is* the zone, so density is set outright
  // and the same weight of growth goes on both parallax layers — the canopy
  // has to close over the player, not stand beside him.
  const stands = rng.int(5, 8);
  for (let s = 0; s < stands; s++) {
    const cx = x0 + rng.range(0, WORLD.chunkSize);
    const standScale = rng.range(0.85, 1.4);
    const blades = rng.int(8, 14);
    for (let i = 0; i < blades; i++) {
      const x = cx + rng.range(-6.5, 6.5);
      const y = seat(x);
      if (y === null) continue;
      out.push({
        kind: 'kelp',
        x, y: y - 0.5,
        z: rng.range(-13, 8),
        scale: rng.range(5, 11) * standScale,
        rot: rng.range(-0.3, 0.3),
        phase: rng.range(0, Math.PI * 2),
        tint: kelpTint(rng),
        layer: 0,
        variant: 0,
      });
    }
  }

  // Understory between the stipes.
  const corals = rng.int(3, 6);
  for (let i = 0; i < corals; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null) continue;
    out.push({
      kind: 'coral',
      x, y: y - 0.2,
      z: rng.range(-8, 4),
      scale: rng.range(1.0, 2.8),
      rot: rng.range(0, Math.PI),
      phase: rng.range(0, Math.PI * 2),
      tint: [0.58 + rng.range(0, 0.34), 0.34 + rng.range(0, 0.26), 0.18 + rng.range(0, 0.22)],
      layer: 0,
      variant: 0,
    });
  }
  scatterRocks(out, rng, x0, rng.int(1, 3), [0.21, 0.23, 0.20]);
  foreground(out, rng, x0, 0.4, [0.07, 0.10, 0.08]);
  // More medusae than anywhere else — they carry the shafts of light.
  // Three to six was the densest decorative bloom in the game and every one
  // of them was unshootable. Most of it is live now — see `MEDUSA` in
  // entities.ts — and what stays is the handful that gives the band depth.
  jellies(out, rng, x0, rng.int(1, 2), 70, () => [0.3, 0.85, 0.9]);

  for (let li = 1; li < Math.min(3, LAYERS.length); li++) {
    const L = LAYERS[li];
    const far = li > 1;
    const z = L.zFront + (far ? 8 : 3);
    const bgStands = far ? rng.int(3, 5) : rng.int(7, 10);
    for (let s = 0; s < bgStands; s++) {
      const cx = x0 + rng.range(0, WORLD.chunkSize);
      if (!rootVisible(cx, L)) continue;
      const blades = rng.int(far ? 6 : 9, far ? 10 : 15);
      const standScale = rng.range(1.2, 1.9) * (far ? 1.35 : 1);
      for (let i = 0; i < blades; i++) {
        const x = cx + rng.range(-12, 12);
        const y = bgSeat(x, L);
        if (y === null) continue;
        out.push({
          kind: 'kelp',
          x, y: y - 1.2,
          z: z + rng.range(-7, 7),
          // Capped: a plant much taller than the ridge it sits on leaves its
          // crown floating in open water once the root is hidden.
          scale: Math.min(11, rng.range(4.5, 8) * standScale),
          rot: rng.range(-0.45, 0.45),
          phase: rng.range(0, Math.PI * 2),
          tint: kelpTint(rng, far ? 0.5 : 0.72),
          layer: li,
          variant: 0,
        });
      }
    }
  }
  backgroundClumps(out, rng, x0, [2, 4], [1, 3], (rng2, far) => ({
    kind: rng2.next() < 0.5 ? 'coral' : 'rock',
    scale: rng2.range(2.0, 5.5) * (far ? 1.4 : 1),
    rot: rng2.range(0, Math.PI * 2),
    tint: far ? [0.14, 0.20, 0.15] : [0.19, 0.26, 0.19],
    variant: 0,
  }));
}

// ---------------------------------------------------------------------------
// SUNKEN CITY — masonry the sea has taken back
// ---------------------------------------------------------------------------

/** Cold, wet, quarried stone. The city has no warmth left in it. */
function stoneTint(rng: Rng, k = 1): RGB {
  const v = rng.range(0.8, 1.2) * k;
  return [0.16 * v, 0.19 * v, 0.23 * v];
}

function sunkenCity(out: Prop[], rng: Rng, x0: number) {
  // Fallen masonry along the streets and heaped against the tower feet. Scale
  // spans a wide range on purpose: a drum, a wall and a whole collapsed arch
  // are the same mesh at three sizes, and the spread is what sells the ruin.
  const ruins = rng.int(4, 7);
  for (let i = 0; i < ruins; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null || !headroom(x, y, 3)) continue;
    out.push({
      kind: 'ruin',
      x, y: y - 0.4,
      z: rng.range(-12, 5),
      scale: rng.range(2, 6),
      rot: rng.range(0, Math.PI * 2),
      phase: rng.range(0, Math.PI * 2),
      tint: stoneTint(rng),
      layer: 0,
      variant: rng.int(0, 2),
    });
  }

  // Rubble collects in drifts against whatever stopped it, never evenly.
  const drifts = rng.int(1, 2);
  for (let d = 0; d < drifts; d++) {
    const cx = x0 + rng.range(0, WORLD.chunkSize);
    const pieces = rng.int(2, 5);
    for (let i = 0; i < pieces; i++) {
      const x = cx + rng.range(-8, 8);
      const y = seat(x);
      if (y === null) continue;
      out.push({
        kind: 'debris',
        x, y: y + rng.range(-0.2, 0.6),
        z: rng.range(-9, 4),
        scale: rng.range(0.9, 2.4),
        rot: rng.range(0, Math.PI * 2),
        phase: 0,
        tint: stoneTint(rng, 0.9),
        layer: 0,
        variant: rng.int(0, 2),
      });
    }
  }

  // Coral recolonising the stone — the only living colour in the zone, so it
  // is kept cold enough not to look like a coastal reef washed in.
  const corals = rng.int(1, 3);
  for (let i = 0; i < corals; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null) continue;
    out.push({
      kind: 'coral',
      x, y: y - 0.2,
      z: rng.range(-8, 3),
      scale: rng.range(0.8, 2.0),
      rot: rng.range(0, Math.PI),
      phase: rng.range(0, Math.PI * 2),
      tint: [0.34 + rng.range(0, 0.16), 0.26 + rng.range(0, 0.14), 0.44 + rng.range(0, 0.18)],
      layer: 0,
      variant: 0,
    });
  }
  scatterRocks(out, rng, x0, rng.int(0, 2), [0.17, 0.19, 0.21]);
  foreground(out, rng, x0, 0.35, [0.06, 0.07, 0.09]);
  jellies(out, rng, x0, rng.int(0, 1), 80, () => [0.3, 0.55, 1.0]);

  // Ruins on the parallax layers too, and much larger: scattered blocks behind
  // the towers read as debris, a skyline reads as a city.
  backgroundClumps(out, rng, x0, [2, 4], [1, 3], (rng2, far) => (
    rng2.next() < 0.7
      ? {
        kind: 'ruin' as const,
        scale: rng2.range(5, 9) * (far ? 1.3 : 1),
        rot: rng2.range(0, Math.PI * 2),
        tint: stoneTint(rng2, far ? 0.7 : 0.85),
        variant: rng2.int(0, 2),
      }
      : {
        kind: 'rock' as const,
        scale: rng2.range(3, 7) * (far ? 1.4 : 1),
        rot: rng2.range(0, Math.PI * 2),
        tint: far ? [0.13, 0.16, 0.19] : [0.16, 0.19, 0.22],
        variant: 0,
      }
  ));
}

// ---------------------------------------------------------------------------
// MILITARY ZONE — a battlefield, and nothing grows on one
// ---------------------------------------------------------------------------

function militaryZone(out: Prop[], rng: Rng, x0: number) {
  // Wreckage is the ground cover here: several drifts per chunk, since the
  // whole zone has to read as something that was fought over and lost.
  const drifts = rng.int(2, 3);
  for (let d = 0; d < drifts; d++) {
    const cx = x0 + rng.range(0, WORLD.chunkSize);
    const pieces = rng.int(3, 6);
    for (let i = 0; i < pieces; i++) {
      const x = cx + rng.range(-9, 9);
      const y = seat(x);
      if (y === null) continue;
      const v = rng.range(0.85, 1.15);
      out.push({
        kind: 'debris',
        x, y: y + rng.range(-0.3, 0.7),
        z: rng.range(-10, 5),
        scale: rng.range(1.0, 3.0),
        rot: rng.range(0, Math.PI * 2),
        phase: 0,
        tint: [0.13 * v, 0.135 * v, 0.13 * v],
        layer: 0,
        variant: rng.int(0, 2),
      });
    }
  }

  // A few emplacements broken up beyond recognition.
  const ruins = rng.int(0, 2);
  for (let i = 0; i < ruins; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null || !headroom(x, y, 3)) continue;
    out.push({
      kind: 'ruin',
      x, y: y - 0.4,
      z: rng.range(-10, 4),
      scale: rng.range(2, 4.5),
      rot: rng.range(0, Math.PI * 2),
      phase: rng.range(0, Math.PI * 2),
      tint: [0.15, 0.155, 0.15],
      layer: 0,
      variant: rng.int(0, 2),
    });
  }

  // One straggling weed, occasionally. Absolute sterility looks like a bug.
  if (rng.next() < 0.16) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y !== null) {
      out.push({
        kind: 'coral',
        x, y: y - 0.2,
        z: rng.range(-7, 3),
        scale: rng.range(0.7, 1.6),
        rot: rng.range(0, Math.PI),
        phase: rng.range(0, Math.PI * 2),
        tint: [0.24, 0.26, 0.22],
        layer: 0,
        variant: 0,
      });
    }
  }
  scatterRocks(out, rng, x0, rng.int(1, 3), [0.155, 0.16, 0.165]);
  foreground(out, rng, x0, 0.45, [0.05, 0.06, 0.065]);
  jellies(out, rng, x0, 0, 70, () => [0.25, 0.5, 0.9]);

  backgroundClumps(out, rng, x0, [2, 4], [1, 3], (rng2, far) => (
    rng2.next() < 0.35
      ? {
        kind: 'debris' as const,
        scale: rng2.range(3, 6) * (far ? 1.3 : 1),
        rot: rng2.range(0, Math.PI * 2),
        tint: far ? [0.10, 0.105, 0.10] : [0.13, 0.135, 0.13],
        variant: rng2.int(0, 2),
      }
      : {
        kind: 'rock' as const,
        scale: rng2.range(2.5, 6) * (far ? 1.4 : 1),
        rot: rng2.range(0, Math.PI * 2),
        tint: far ? [0.12, 0.13, 0.14] : [0.15, 0.16, 0.17],
        variant: 0,
      }
  ));
}

// ---------------------------------------------------------------------------
// THERMAL VENTS — the one warm zone
// ---------------------------------------------------------------------------

/** Rust and sulphur: iron oxide reds through to pale brimstone yellow. */
function ventTint(rng: Rng, k = 1): RGB {
  const t = rng.next();
  return t < 0.55
    ? [0.44 * k * rng.range(0.85, 1.2), 0.24 * k, 0.12 * k]
    : [0.52 * k, 0.46 * k * rng.range(0.9, 1.15), 0.18 * k];
}

function thermalVents(out: Prop[], rng: Rng, x0: number) {
  const x1 = x0 + WORLD.chunkSize;

  // Cluster tightly on the authored chimneys: the props and the rock stacks
  // have to read as one formation, not as decoration standing near it. Each
  // authored vent belongs to exactly one chunk, so nothing is placed twice.
  for (const vx of VENT_X) {
    if (vx < x0 || vx >= x1) continue;
    const n = rng.int(3, 4);
    for (let i = 0; i < n; i++) {
      const x = vx + rng.range(-17, 17);
      const y = seat(x);
      if (y === null || !headroom(x, y, 2.5)) continue;
      out.push({
        kind: 'vent',
        x, y: y - 0.3,
        z: rng.range(-9, 3),
        scale: rng.range(1.2, 2.2),
        rot: rng.range(0, Math.PI * 2),
        phase: rng.range(0, Math.PI * 2),
        tint: ventTint(rng),
        layer: 0,
        variant: 0,
      });
    }
    // Tube worms colonise the warm ground right at the foot of a stack.
    const worms = rng.int(2, 4);
    for (let i = 0; i < worms; i++) {
      const x = vx + rng.range(-22, 22);
      const y = seat(x);
      if (y === null) continue;
      const pale = rng.next() < 0.45;
      out.push({
        kind: 'coral',
        x, y: y - 0.2,
        z: rng.range(-7, 3),
        scale: rng.range(0.7, 1.8),
        rot: rng.range(0, Math.PI),
        phase: rng.range(0, Math.PI * 2),
        tint: pale
          ? [0.72, 0.68, 0.58]
          : [0.62 + rng.range(0, 0.24), 0.12 + rng.range(0, 0.08), 0.10],
        layer: 0,
        variant: 0,
      });
    }
  }

  // Smaller strays away from the authored stacks, so the field is not just the
  // nine landmarks with bare ground between them.
  const strays = rng.next() < 0.7 ? rng.int(1, 2) : 0;
  for (let i = 0; i < strays; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null || !headroom(x, y, 2.5)) continue;
    out.push({
      kind: 'vent',
      x, y: y - 0.3,
      z: rng.range(-10, 3),
      scale: rng.range(0.8, 1.5),
      rot: rng.range(0, Math.PI * 2),
      phase: rng.range(0, Math.PI * 2),
      tint: ventTint(rng, 0.9),
      layer: 0,
      variant: 0,
    });
  }

  const drifts = rng.next() < 0.5 ? rng.int(1, 3) : 0;
  for (let i = 0; i < drifts; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null) continue;
    out.push({
      kind: 'debris',
      x, y: y + rng.range(-0.2, 0.4),
      z: rng.range(-8, 3),
      scale: rng.range(0.8, 1.8),
      rot: rng.range(0, Math.PI * 2),
      phase: 0,
      tint: [0.26, 0.19, 0.12],
      layer: 0,
      variant: rng.int(0, 2),
    });
  }
  scatterRocks(out, rng, x0, rng.int(1, 3), [0.26, 0.19, 0.13]);
  foreground(out, rng, x0, 0.4, [0.10, 0.07, 0.045]);
  jellies(out, rng, x0, rng.int(0, 2), 70, () => [1.0, 0.55, 0.25]);

  backgroundClumps(out, rng, x0, [2, 4], [1, 3], (rng2, far) => ({
    kind: 'rock' as const,
    scale: rng2.range(2.5, 6) * (far ? 1.4 : 1),
    rot: rng2.range(0, Math.PI * 2),
    tint: far ? ventTint(rng2, 0.34) : ventTint(rng2, 0.5),
    variant: 0,
  }));
}

// ---------------------------------------------------------------------------
// THE ABYSS — the emptiness is the content
// ---------------------------------------------------------------------------

function theAbyss(out: Prop[], rng: Rng, x0: number) {
  // Everything here is a rare event rather than a field. Filling this stretch
  // would cost the zone the only thing it has, which is scale.
  scatterRocks(out, rng, x0, rng.int(0, 2), [0.09, 0.10, 0.115]);

  if (rng.next() < 0.3) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y !== null) {
      out.push({
        kind: 'debris',
        x, y: y + rng.range(-0.2, 0.5),
        z: rng.range(-9, 3),
        scale: rng.range(0.9, 2.2),
        rot: rng.range(0, Math.PI * 2),
        phase: 0,
        tint: [0.10, 0.11, 0.12],
        layer: 0,
        variant: rng.int(0, 2),
      });
    }
  }

  // A single medusa in a hundred metres of black water is an event; a shoal of
  // them would just be lighting.
  if (rng.next() < 0.35) {
    jellies(out, rng, x0, 1, 90, (r) => (
      r.next() < 0.5 ? [0.30, 0.95, 0.85] : [0.55, 0.35, 1.0]
    ));
  }
  foreground(out, rng, x0, 0.22, [0.03, 0.035, 0.045]);

  // One clump every few chunks: enough that the far ridge is not a bare
  // cut-out, far too little to populate it.
  if (rng.next() < 0.4) {
    backgroundClumps(out, rng, x0, [1, 2], [0, 1], (rng2, far) => ({
      kind: 'rock' as const,
      scale: rng2.range(3, 7) * (far ? 1.4 : 1),
      rot: rng2.range(0, Math.PI * 2),
      tint: far ? [0.07, 0.08, 0.10] : [0.09, 0.10, 0.12],
      variant: 0,
    }));
  }
}

// ---------------------------------------------------------------------------
// ANCIENT STRUCTURE — architecture, so nothing is scattered
// ---------------------------------------------------------------------------

/** Pale, cold, faintly teal — the stone is lit by something, not by the sun. */
function ancientTint(rng: Rng, k = 1): RGB {
  const v = rng.range(0.9, 1.1) * k;
  return [0.30 * v, 0.36 * v, 0.37 * v];
}

/** Spacing of the colonnade between the authored pillars, metres. */
const COLONNADE = 30;

function nearPillar(x: number, d: number): boolean {
  for (const p of PILLAR_X) if (Math.abs(p - x) < d) return true;
  return false;
}

function ancientStructure(out: Prop[], rng: Rng, x0: number) {
  const x1 = x0 + WORLD.chunkSize;

  // Monoliths flank the authored pillars and then carry on along the same
  // measure. Placed on a grid rather than sampled: the whole point of the zone
  // is that something laid it out, and randomness reads as geology.
  for (const px of PILLAR_X) {
    if (px < x0 || px >= x1) continue;
    for (const side of [-1, 1]) {
      // Clear of the authored pillar itself, which is 13 m through the middle.
      const x = px + side * 19;
      const y = seat(x);
      if (y === null || !headroom(x, y, 3.5)) continue;
      out.push({
        kind: 'monolith',
        x, y: y - 0.5,
        z: side < 0 ? -8 : 4,
        scale: rng.range(1.9, 2.7),
        rot: (Math.PI / 2) * rng.int(0, 3),
        phase: 0,
        tint: ancientTint(rng),
        layer: 0,
        variant: 0,
      });
    }
  }
  for (let x = Math.ceil(x0 / COLONNADE) * COLONNADE; x < x1; x += COLONNADE) {
    // Wide enough to clear the flankers as well as the pillar, so the two
    // rules never plant two monoliths in the same metre of floor.
    if (nearPillar(x, 30)) continue;
    const y = seat(x);
    if (y === null || !headroom(x, y, 3.5)) continue;
    out.push({
      kind: 'monolith',
      x, y: y - 0.5,
      z: rng.range(-9, 3),
      scale: rng.range(1.6, 2.4),
      rot: (Math.PI / 2) * rng.int(0, 3),
      phase: 0,
      tint: ancientTint(rng, 0.92),
      layer: 0,
      variant: 0,
    });
  }

  // Blocks shed by the complex, still squared off.
  const ruins = rng.int(1, 3);
  for (let i = 0; i < ruins; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null || !headroom(x, y, 3)) continue;
    out.push({
      kind: 'ruin',
      x, y: y - 0.4,
      z: rng.range(-11, 4),
      scale: rng.range(2, 5),
      rot: (Math.PI / 2) * rng.int(0, 3) + rng.range(-0.06, 0.06),
      phase: rng.range(0, Math.PI * 2),
      tint: ancientTint(rng, 0.8),
      layer: 0,
      variant: rng.int(0, 2),
    });
  }
  scatterRocks(out, rng, x0, rng.int(0, 2), [0.16, 0.19, 0.20]);
  foreground(out, rng, x0, 0.3, [0.045, 0.055, 0.06]);
  jellies(out, rng, x0, 0, 80, () => [0.45, 0.95, 1.0]);

  // The colonnade recedes: one far monolith now and then, plus rubble, so the
  // background is architecture as well. Kept rare — the play-layer colonnade
  // already spends most of the monolith budget.
  if (rng.next() < 0.3) {
    backgroundClumps(out, rng, x0, [1, 1], [0, 1], (rng2, far) => ({
      kind: 'monolith' as const,
      scale: rng2.range(3, 5) * (far ? 1.3 : 1),
      rot: (Math.PI / 2) * rng2.int(0, 3),
      tint: ancientTint(rng2, far ? 0.6 : 0.75),
      variant: 0,
    }));
  }
  backgroundClumps(out, rng, x0, [1, 2], [0, 2], (rng2, far) => ({
    kind: rng2.next() < 0.45 ? 'ruin' as const : 'rock' as const,
    scale: rng2.range(3, 6.5) * (far ? 1.4 : 1),
    rot: rng2.range(0, Math.PI * 2),
    tint: ancientTint(rng2, far ? 0.5 : 0.65),
    variant: rng2.int(0, 2),
  }));
}

// ---------------------------------------------------------------------------
// Shared placement
// ---------------------------------------------------------------------------

function scatterRocks(out: Prop[], rng: Rng, x0: number, count: number, tint: RGB) {
  for (let i = 0; i < count; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const y = seat(x);
    if (y === null) continue;
    out.push({
      kind: 'rock',
      x, y: y + rng.range(-0.6, 0.4),
      z: rng.range(-10, 2),
      scale: rng.range(0.8, 3.4),
      rot: rng.range(0, Math.PI * 2),
      phase: 0,
      tint: [tint[0], tint[1], tint[2]],
      layer: 0,
      variant: 0,
    });
  }
}

/** Foreground silhouette — close to the camera, deliberately unlit. */
function foreground(out: Prop[], rng: Rng, x0: number, chance: number, tint: RGB) {
  if (rng.next() >= chance) return;
  const x = x0 + rng.range(0, WORLD.chunkSize);
  out.push({
    kind: 'fgrock',
    x,
    // Kept low in frame so they read as framing, not obstructions.
    y: rng.range(seabedY(x) - 10, seabedY(x) + 6),
    z: rng.range(13, 23),
    scale: rng.range(1.8, 4.4),
    rot: rng.range(0, Math.PI * 2),
    phase: 0,
    tint: [tint[0], tint[1], tint[2]],
    layer: 0,
    variant: 0,
  });
}

/**
 * Decorative medusae only — anything in the play plane is a live creature owned
 * by the actor system, so it can be shot and can sting.
 */
function jellies(
  out: Prop[], rng: Rng, x0: number, count: number, span: number,
  tint: (rng: Rng) => RGB,
) {
  // AND MORE OF THEM THE DEEPER IT GETS, which the background already had.
  //
  // `bgJellies` scales its population with depth and wins the argument for
  // doing so: down where the ambient is a dome and there is nothing else to see
  // by, a glowing animal is the only structure the water has. Every word of
  // that applies to the lane the player is actually flying through, and the
  // near band never got it — the zones author 0 to 2 per chunk flat while the
  // background puts five in each of two layers. Ten to one, and then parallax
  // makes each of the ten bigger.
  //
  // Derived here rather than plumbed through six zone functions that take only
  // `x0`. It is the same quantity the switch computes, from the same midpoint.
  const depth = -seabedY(x0 + WORLD.chunkSize * 0.5);
  const deep = Math.round(Math.min(1, depth / 900) * 3);
  for (let i = 0; i < count + deep; i++) {
    const x = x0 + rng.range(0, WORLD.chunkSize);
    const top = seabedY(x);
    const y = rng.range(top + 6, Math.min(WORLD.surfaceY - 8, top + span));
    if (!isClear(x, y, 3)) continue;
    out.push({
      kind: 'jelly',
      x, y,
      // BACK, AND BIG.
      //
      // I moved this band forward to -26 to fix "the near jellyfish are small",
      // and that was the wrong fix for the right complaint. The small near
      // animals were the LIVE ones — `configure` had no case for them and they
      // were all coming out at the default size of 1 — and pulling the
      // decoration forward to compensate put a crowd of UNSHOOTABLE medusae in
      // the closest, brightest part of the frame. "Loads of them and I can't
      // kill any" is that change, precisely.
      //
      // So it goes back behind, near enough to lane 1 at -55 to read as the
      // same distance, and stays big because it is far. What is near, bright
      // and worth aiming at is now the live population, which is what the
      // player was reaching for all along.
      z: rng.range(-66, -42),
      scale: rng.range(1.6, 3.8),
      rot: 0,
      phase: rng.range(0, Math.PI * 2),
      tint: tint(rng),
      layer: 0,
      variant: rng.int(0, JELLY_SPECIES - 1),
    });
  }
}

/**
 * Bioluminescence in the BACKGROUND lanes, which had none at all.
 *
 * `jellies` writes `layer: 0` without exception, so every glowing thing in the
 * game was in the water the player is flying through. The parallax lanes got
 * rock, weed and silhouettes — nothing that makes its own light — so at depth,
 * where the ambient is a dome and there is nothing else to see by, the whole
 * background was one flat dark wall.
 *
 * A light in the distance is the cheapest depth cue there is. It says how far
 * away the far wall is, which no amount of shading a wall can, and it says it
 * even when the wall itself is too dark to make out.
 *
 * DEEP ZONES ONLY, and more of them the deeper it gets. Near the surface there
 * is plenty of light to read distance by and a glowing thing back there is just
 * a bright speck; at two thousand metres it is the only structure the water
 * has.
 */
function bgJellies(out: Prop[], rng: Rng, x0: number, depth: number, tint: (rng: Rng) => RGB) {
  // Three, not five. Five in each of two layers put ten glowing animals per
  // chunk behind the player against three in front, and the background stopped
  // being a depth cue and became the subject.
  const want = Math.round(Math.min(1, depth / 900) * 3);
  for (let li = 1; li < Math.min(3, LAYERS.length); li++) {
    const L = LAYERS[li]!;
    for (let i = 0; i < want; i++) {
      const x = x0 + rng.range(0, WORLD.chunkSize);
      const seat = bgSeat(x, L);
      if (seat === null) continue;
      out.push({
        kind: 'jelly',
        x,
        y: seat + rng.range(10, 74),
        z: laneZ(li) + rng.range(-9, 4),
        // Bigger than the play lane's, because they are further away and a
        // background light that reads as a pinprick reads as a dead pixel.
        scale: rng.range(1.6, 3.8),
        rot: 0,
        phase: rng.range(0, Math.PI * 2),
        tint: tint(rng),
        layer: li,
        variant: rng.int(0, JELLY_SPECIES - 1),
      });
    }
  }
}

/**
 * Silhouette pass on the parallax layers. Every zone needs something standing
 * on the far ridge — a bare cut-out collapses the depth read — but what that
 * something is changes: rubble in the city, wreckage in the military zone,
 * boulders in the deep.
 */
function backgroundClumps(
  out: Prop[], rng: Rng, x0: number,
  near: [number, number], far: [number, number],
  pick: (rng: Rng, far: boolean) => { kind: PropKind; scale: number; rot: number; tint: RGB; variant: number },
) {
  for (let li = 1; li < Math.min(3, LAYERS.length); li++) {
    const L = LAYERS[li];
    const isFar = li > 1;
    const z = L.zFront + (isFar ? 8 : 3);
    const [lo, hi] = isFar ? far : near;
    const count = rng.int(lo, hi);
    for (let i = 0; i < count; i++) {
      const x = x0 + rng.range(0, WORLD.chunkSize);
      if (!rootVisible(x, L)) continue;
      const y = bgSeat(x, L);
      if (y === null) continue;
      const p = pick(rng, isFar);
      out.push({
        kind: p.kind,
        x, y: y + rng.range(-1.8, 0.6),
        z: z + rng.range(-9, 5),
        scale: p.scale,
        rot: p.rot,
        phase: rng.range(0, Math.PI * 2),
        tint: p.tint,
        layer: li,
        variant: p.variant,
      });
    }
  }
}

/**
 * Vegetation on the first parallax layer. Without it the mid-ground reads as a
 * bare silhouette and the depth cue collapses — the eye needs the same kind of
 * detail behind the play plane as in front of it, just smaller and dimmer.
 *
 * The background terrain is the same field sampled at `x + warp` and lifted by
 * `yOffset`, so its floor is found the same way.
 */
function addBackgroundGrowth(out: Prop[], rng: Rng, x0: number, depth: number) {
  // Layer 1 carries most of it; layer 2 gets a sparse silhouette pass so the
  // far ridge is not a bare cut-out either.
  for (let li = 1; li < Math.min(3, LAYERS.length); li++) {
    const L = LAYERS[li];
    const far = li > 1;
    const z = L.zFront + (far ? 8 : 3);
    // Growth persists deeper on the parallax layers than on the play plane:
    // these are read as distant terrain, not as things you swim through.
    const density = Math.max(far ? 0.08 : 0.14, 1 - depth / 520);

    const stands = Math.round(density * rng.range(...(far ? [1.5, 4] : [4, 9]) as [number, number]));
    for (let s = 0; s < stands; s++) {
      const cx = x0 + rng.range(0, WORLD.chunkSize);
      if (!rootVisible(cx, L)) continue;
      const blades = rng.int(far ? 4 : 6, far ? 9 : 16);
      const standScale = rng.range(1.1, 1.9) * (far ? 1.35 : 1);
      for (let i = 0; i < blades; i++) {
        const x = cx + rng.range(-11, 11);
        // Seat every blade, not just the stand's centre. `rootVisible` above
        // asks whether this patch of background ridge can be seen at all, which
        // varies slowly; whether a given blade has ground under it varies over
        // a metre, and blades scatter 11 m either side of that centre.
        //
        // It has to go through `bgSeat` rather than a bare `floorAt`. `floorAt`
        // searches down from `seabedY + 90` and returns the first rock it meets,
        // which on a tower or an arch is the crown — a real surface, but not one
        // anything grows on. `seat` rejects those for the play layer and this
        // never did, so kelp took root on arch tops; and because the layer is
        // warped by hundreds of metres, the arch holding it up is off screen and
        // the stand reads as a canopy of weed hanging in open water.
        const y = bgSeat(x, L);
        if (y === null) continue;
        out.push({
          kind: 'kelp',
          // Capped height: if a plant is much taller than the ridge it sits on,
          // a hidden root leaves the crown floating in open water.
          x, y: y - 1.2,
          z: z + rng.range(-7, 7),
          scale: Math.min(8, rng.range(2.8, 6.2) * standScale * (0.5 + density * 0.7)),
          rot: rng.range(-0.45, 0.45),
          phase: rng.range(0, Math.PI * 2),
          tint: far
            ? [0.030, 0.055, 0.038]
            : [0.055 + rng.range(0, 0.030), 0.105 + rng.range(0, 0.060), 0.058 + rng.range(0, 0.032)],
          layer: li,
          variant: 0,
        });
      }
    }

    const clumps = rng.int(far ? 1 : 3, far ? 4 : 8);
    for (let i = 0; i < clumps; i++) {
      const x = x0 + rng.range(0, WORLD.chunkSize);
      if (!rootVisible(x, L)) continue;
      const y = bgSeat(x, L);
      if (y === null) continue;
      out.push({
        kind: rng.next() < 0.55 ? 'coral' : 'rock',
        x, y: y + rng.range(-1.8, 0.6),
        z: z + rng.range(-9, 5),
        scale: rng.range(2.0, 6) * (far ? 1.4 : 1),
        rot: rng.range(0, Math.PI * 2),
        phase: rng.range(0, Math.PI * 2),
        tint: far ? [0.045, 0.062, 0.082] : [0.088, 0.108, 0.128],
        layer: li,
        variant: 0,
      });
    }
  }
}
